// Brisbane (AEST) is UTC+10 all year — Queensland has NO daylight saving — so a
// fixed offset is always correct. These helpers make <input type="datetime-local">
// values render and save in Brisbane time regardless of the device's own
// timezone, so scheduling can't shift when an admin's laptop clock isn't Brisbane
// (or when scheduling happens server-side, where there is no browser timezone).

const BRISBANE_OFFSET = '+10:00';

/** An instant (ISO string or Date) → "YYYY-MM-DDTHH:mm" Brisbane wall-clock,
 *  for a <input type="datetime-local"> value. */
export function toBrisbaneInput(instant: string | Date): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(d.getTime())) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  })
    .formatToParts(d)
    .reduce((a: Record<string, string>, x) => ((a[x.type] = x.value), a), {});
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** A datetime-local value ("YYYY-MM-DDTHH:mm", entered as Brisbane wall-clock)
 *  → a UTC ISO instant. Interprets the input as Brisbane (+10:00), NOT the
 *  device's timezone. Returns '' for an empty/invalid value. */
export function brisbaneInputToIso(local: string): string {
  if (!local) return '';
  const withSecs = local.length === 16 ? `${local}:00` : local;
  const d = new Date(`${withSecs}${BRISBANE_OFFSET}`);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** Brisbane wall-clock "now + n days at hh:mm" as a datetime-local string.
 *  Used to pre-fill schedule pickers with a sensible Brisbane default. */
export function brisbaneDefaultInput(addDays = 1, hour = 8, minute = 0): string {
  const base = toBrisbaneInput(new Date()); // today's Brisbane date + current time
  const [datePart] = base.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  // Shift the date by addDays using a UTC anchor (date-only math, DST-free zone).
  const shifted = new Date(Date.UTC(y, m - 1, d + addDays));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}T${pad(hour)}:${pad(minute)}`;
}
